import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

interface ProjectDetailData {
  id: string;
  title: string;
  category: string;
  description: string;
  fullDescription: string;
  image: string;
  gallery: string[];
  specs: {
    size: string;
    duration: string;
    materials: string;
    features: string[];
  };
  client: string;
  location: string;
  year: string;
  featured: boolean;
  caseStudy: {
    scope: string;
    challenges: string[];
    solutions: string[];
    timeline: string;
    outcomes: string[];
    measurableResults: string[];
  };
}

const projectDetails: ProjectDetailData[] = [
  {
    id: "modern-luxury-home",
    title: "Modern Luxury Home - Newport Beach",
    category: "Residential",
    description: "Contemporary rough framing for a stunning 5,000 sq ft luxury residence with complex architectural elements",
    fullDescription: "This modern luxury home project in Newport Beach showcased our expertise in complex architectural rough framing. The project featured soaring 20-foot ceilings, custom engineered trusses, and intricate roof framing that required precise engineering and craftsmanship. We worked closely with the architect to ensure every structural detail was executed to perfection, completing the rough framing 20% faster than industry average while maintaining the highest quality standards.",
    image: "/images/project-luxury-home.jpg",
    gallery: [
      "/images/project-luxury-home-1.jpg",
      "/images/project-luxury-home-2.jpg",
      "/images/project-luxury-home-3.jpg",
      "/images/project-luxury-home-4.jpg"
    ],
    specs: {
      size: "5,000 sq ft",
      duration: "8 weeks",
      materials: "Premium Douglas Fir, Engineered Lumber, Steel Beams",
      features: ["Custom Engineered Trusses", "20-ft Vaulted Ceilings", "Complex Roof Design", "Energy Efficient", "Seismic Compliance"]
    },
    client: "Private Residence",
    location: "Newport Beach, CA",
    year: "2024",
    featured: true,
    caseStudy: {
      scope: "Complete rough framing for a 5,000 sq ft luxury home including complex architectural elements, custom trusses, and seismic compliance requirements.",
      challenges: [
        "Complex architectural design with 20-foot vaulted ceilings",
        "Custom engineered trusses requiring precise fabrication",
        "Seismic compliance for coastal location",
        "Tight timeline with multiple trade coordination",
        "High-end client expectations for quality and finish"
      ],
      solutions: [
        "Pre-fabricated custom trusses off-site for precision",
        "Advanced 3D modeling for complex roof geometry",
        "Enhanced seismic bracing system",
        "Dedicated project manager for coordination",
        "Quality control checkpoints at each phase"
      ],
      timeline: "8 weeks (20% faster than industry average)",
      outcomes: [
        "Completed 20% faster than industry average",
        "Zero structural issues during inspection",
        "Exceeded client expectations for quality",
        "Received commendation from city building department",
        "Project completed under budget"
      ],
      measurableResults: [
        "20% faster completion time",
        "100% inspection pass rate",
        "15% under budget",
        "Zero safety incidents",
        "Client satisfaction score: 10/10"
      ]
    }
  },
  {
    id: "downtown-office-complex",
    title: "Downtown Irvine Office Complex",
    category: "Commercial",
    description: "Multi-story commercial rough framing with modern architectural elements and LEED certification requirements",
    fullDescription: "This downtown Irvine office complex required sophisticated rough framing techniques to accommodate the building's unique architectural design and LEED certification requirements. The project included three stories with varying floor plans, large open spaces, and structural elements that needed to support heavy loads while maintaining aesthetic appeal. We completed the rough framing phase 15% under budget and two weeks ahead of schedule, earning praise from the general contractor and architect.",
    image: "/images/project-office-complex.jpg",
    gallery: [
      "/images/project-office-1.jpg",
      "/images/project-office-2.jpg",
      "/images/project-office-3.jpg",
      "/images/project-office-4.jpg"
    ],
    specs: {
      size: "25,000 sq ft",
      duration: "12 weeks",
      materials: "Steel Frame, Engineered Beams, Sustainable Lumber",
      features: ["Multi-Story", "Open Floor Plans", "Structural Steel", "Modern Design", "LEED Certified", "Seismic Bracing"]
    },
    client: "Irvine Development Corp",
    location: "Irvine, CA",
    year: "2023",
    featured: true,
    caseStudy: {
      scope: "Multi-story commercial office complex with LEED certification requirements, open floor plans, and modern architectural elements.",
      challenges: [
        "LEED certification requirements for sustainable materials",
        "Complex multi-story structural design",
        "Large open floor plans requiring specialized support",
        "Tight coordination with multiple trades",
        "Budget constraints with high-end finishes"
      ],
      solutions: [
        "Sourced sustainable lumber from certified suppliers",
        "Engineered steel frame system for open spaces",
        "Modular framing approach for efficiency",
        "Daily coordination meetings with all trades",
        "Value engineering to meet budget requirements"
      ],
      timeline: "12 weeks (2 weeks ahead of schedule)",
      outcomes: [
        "Completed 2 weeks ahead of schedule",
        "15% under budget",
        "Achieved LEED certification requirements",
        "Zero change orders during framing phase",
        "Excellent coordination with other trades"
      ],
      measurableResults: [
        "2 weeks ahead of schedule",
        "15% under budget",
        "100% LEED material compliance",
        "Zero safety incidents",
        "General contractor satisfaction: 9.5/10"
      ]
    }
  },
  {
    id: "custom-deck-pergola",
    title: "Custom Deck & Pergola - Laguna Beach",
    category: "Custom",
    description: "Handcrafted outdoor living space with premium wood rough framing and custom architectural elements",
    fullDescription: "This custom outdoor living project in Laguna Beach combined traditional woodworking techniques with modern construction methods. The pergola features hand-carved details and custom joinery, while the deck incorporates sustainable materials and innovative drainage systems. The project required precise rough framing to support the complex architectural design while withstanding coastal weather conditions. We completed the project 25% faster than estimated while exceeding quality expectations.",
    image: "/images/project-deck-pergola.jpg",
    gallery: [
      "/images/project-deck-1.jpg",
      "/images/project-deck-2.jpg",
      "/images/project-deck-3.jpg",
      "/images/project-deck-4.jpg"
    ],
    specs: {
      size: "800 sq ft",
      duration: "3 weeks",
      materials: "Cedar, Pressure Treated Lumber, Marine-Grade Hardware",
      features: ["Custom Joinery", "Hand-Carved Details", "Integrated Lighting", "Drainage System", "Coastal Weather Resistant", "Seismic Bracing"]
    },
    client: "Private Residence",
    location: "Laguna Beach, CA",
    year: "2024",
    featured: true,
    caseStudy: {
      scope: "Custom outdoor living space with hand-crafted pergola, deck, and integrated features for coastal environment.",
      challenges: [
        "Coastal weather conditions requiring special materials",
        "Complex hand-carved architectural details",
        "Integration of lighting and drainage systems",
        "Seismic requirements for elevated structure",
        "Client's high expectations for craftsmanship"
      ],
      solutions: [
        "Marine-grade hardware and pressure-treated materials",
        "Pre-fabricated custom joinery in controlled environment",
        "Integrated drainage system with hidden components",
        "Enhanced seismic bracing for elevated structure",
        "Dedicated craftsman for hand-carved details"
      ],
      timeline: "3 weeks (25% faster than estimated)",
      outcomes: [
        "Completed 25% faster than estimated",
        "Exceeded quality expectations",
        "Perfect integration of all systems",
        "Client delighted with craftsmanship",
        "Zero issues with coastal weather exposure"
      ],
      measurableResults: [
        "25% faster completion",
        "100% weather resistance",
        "Zero structural issues",
        "Client satisfaction: 10/10",
        "Perfect drainage system performance"
      ]
    }
  },
  {
    id: "retail-space-framing",
    title: "Huntington Beach Retail Space",
    category: "Commercial",
    description: "Efficient rough framing solution for modern retail environment with flexible layout requirements",
    fullDescription: "This Huntington Beach retail space project required flexible rough framing that could accommodate various store layouts and future modifications. We implemented modular framing techniques that allow for easy reconfiguration while maintaining structural integrity. The project was completed 30% under budget and one week ahead of schedule, allowing the retail tenant to open earlier than planned.",
    image: "/images/project-retail-space.jpg",
    gallery: [
      "/images/project-retail-1.jpg",
      "/images/project-retail-2.jpg",
      "/images/project-retail-3.jpg",
      "/images/project-retail-4.jpg"
    ],
    specs: {
      size: "8,000 sq ft",
      duration: "6 weeks",
      materials: "Engineered Lumber, Steel Supports, Modular Components",
      features: ["Modular Design", "Flexible Layout", "Load Bearing", "Future-Ready", "Quick Assembly", "Cost Effective"]
    },
    client: "Huntington Beach Retail Chain",
    location: "Huntington Beach, CA",
    year: "2023",
    featured: false,
    caseStudy: {
      scope: "Flexible retail space framing with modular design for easy reconfiguration and future modifications.",
      challenges: [
        "Need for flexible layout to accommodate different tenants",
        "Budget constraints for retail development",
        "Quick turnaround required for tenant move-in",
        "Future modification requirements",
        "Cost-effective solution needed"
      ],
      solutions: [
        "Modular framing system for easy reconfiguration",
        "Pre-engineered components for quick assembly",
        "Standardized connection points for flexibility",
        "Efficient material usage to reduce costs",
        "Streamlined construction process"
      ],
      timeline: "6 weeks (1 week ahead of schedule)",
      outcomes: [
        "Completed 1 week ahead of schedule",
        "30% under budget",
        "Tenant able to open earlier than planned",
        "Easy reconfiguration for future tenants",
        "Excellent cost-to-value ratio"
      ],
      measurableResults: [
        "1 week ahead of schedule",
        "30% under budget",
        "Tenant opened 2 weeks early",
        "100% flexibility for layout changes",
        "ROI exceeded expectations by 40%"
      ]
    }
  },
  {
    id: "traditional-home-addition",
    title: "Traditional Home Addition - Anaheim",
    category: "Residential",
    description: "Seamless integration of new rough framing with existing structure in historic district",
    fullDescription: "This home addition project in Anaheim's historic district required careful planning to match the existing structure's framing style and materials. We seamlessly integrated the new addition while preserving the home's architectural integrity and ensuring structural compatibility. The project required special permits and careful coordination with the historic preservation board. We completed the rough framing phase with zero damage to the existing structure and received commendation from the city's historic preservation committee.",
    image: "/images/project-home-addition.jpg",
    gallery: [
      "/images/project-addition-1.jpg",
      "/images/project-addition-2.jpg",
      "/images/project-addition-3.jpg",
      "/images/project-addition-4.jpg"
    ],
    specs: {
      size: "1,200 sq ft",
      duration: "4 weeks",
      materials: "Matching Existing Materials, Historic-Compliant Lumber",
      features: ["Seamless Integration", "Structural Compatibility", "Architectural Match", "Minimal Disruption", "Historic Preservation", "Permit Compliance"]
    },
    client: "Private Residence",
    location: "Anaheim, CA",
    year: "2024",
    featured: false,
    caseStudy: {
      scope: "Historic home addition requiring seamless integration with existing structure while meeting preservation requirements.",
      challenges: [
        "Historic district preservation requirements",
        "Matching existing framing style and materials",
        "Zero damage to existing structure",
        "Complex permit process",
        "Coordination with historic preservation board"
      ],
      solutions: [
        "Detailed analysis of existing structure",
        "Sourced matching historic-compliant materials",
        "Careful demolition and protection of existing structure",
        "Expert permit navigation and documentation",
        "Regular communication with preservation board"
      ],
      timeline: "4 weeks (on schedule despite complexity)",
      outcomes: [
        "Zero damage to existing structure",
        "Perfect architectural integration",
        "Received commendation from preservation board",
        "All permits approved without issues",
        "Client delighted with seamless result"
      ],
      measurableResults: [
        "100% preservation compliance",
        "Zero damage to existing structure",
        "Perfect architectural match",
        "All permits approved on first submission",
        "Historic preservation board rating: 10/10"
      ]
    }
  },
  {
    id: "warehouse-framing",
    title: "Santa Ana Industrial Warehouse",
    category: "Commercial",
    description: "Large-scale industrial rough framing for warehouse facility with heavy equipment support",
    fullDescription: "This Santa Ana industrial warehouse project required robust rough framing to support heavy equipment and storage systems. We implemented advanced framing techniques to maximize space efficiency while ensuring structural integrity for long-term use. The project featured 40-foot clear spans and specialized framing to support overhead cranes and heavy machinery. We completed the rough framing phase 20% faster than industry average while maintaining the highest safety standards.",
    image: "/images/project-warehouse.jpg",
    gallery: [
      "/images/project-warehouse-1.jpg",
      "/images/project-warehouse-2.jpg",
      "/images/project-warehouse-3.jpg",
      "/images/project-warehouse-4.jpg"
    ],
    specs: {
      size: "50,000 sq ft",
      duration: "16 weeks",
      materials: "Heavy Timber, Steel Frame, Industrial Hardware",
      features: ["High Load Capacity", "Clear Span Design", "Equipment Support", "Efficient Layout", "Crane Support", "Seismic Compliance"]
    },
    client: "Santa Ana Manufacturing Company",
    location: "Santa Ana, CA",
    year: "2023",
    featured: false,
    caseStudy: {
      scope: "Large-scale industrial warehouse with 40-foot clear spans, overhead crane support, and heavy equipment requirements.",
      challenges: [
        "40-foot clear spans requiring specialized engineering",
        "Overhead crane support system",
        "Heavy equipment load requirements",
        "Large-scale project coordination",
        "Safety requirements for industrial facility"
      ],
      solutions: [
        "Advanced structural engineering for clear spans",
        "Custom crane support system design",
        "Heavy-duty industrial framing materials",
        "Comprehensive safety protocols",
        "Dedicated project management team"
      ],
      timeline: "16 weeks (20% faster than industry average)",
      outcomes: [
        "Completed 20% faster than industry average",
        "Zero safety incidents",
        "All equipment support systems installed perfectly",
        "Exceeded load capacity requirements",
        "Client able to begin operations early"
      ],
      measurableResults: [
        "20% faster completion",
        "Zero safety incidents",
        "100% load capacity compliance",
        "Equipment operational 2 weeks early",
        "Client satisfaction: 9.8/10"
      ]
    }
  }
];

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [currentImage, setCurrentImage] = useState(0);
  const [project, setProject] = useState<ProjectDetailData | null>(null);

  useEffect(() => {
    const foundProject = projectDetails.find(p => p.id === id);
    setProject(foundProject || null);
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Project Not Found</h1>
          <Link to="/projects" className="text-amber-400 hover:text-amber-300">
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>{project.title} | Low Cost Framing by Woodhenge Builders</title>
        <meta name="description" content={project.description} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${project.image})` }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl mx-auto px-6">
            <span className="bg-amber-800 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 inline-block border border-amber-600">
              {project.category}
            </span>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              {project.title}
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 font-light text-amber-100">
              {project.description}
            </p>
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Get Free Quote
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 tracking-tight">
                Project Overview
              </h2>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>{project.fullDescription}</p>
              </div>
              
              <div className="mt-12 grid grid-cols-2 gap-8">
                <div>
                  <div className="text-3xl font-bold text-amber-400 mb-2">{project.specs.size}</div>
                  <div className="text-gray-400">Project Size</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400 mb-2">{project.specs.duration}</div>
                  <div className="text-gray-400">Duration</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400 mb-2">{project.location}</div>
                  <div className="text-gray-400">Location</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400 mb-2">{project.year}</div>
                  <div className="text-gray-400">Year</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-amber-400 mb-4">Project Specifications</h3>
                <div className="bg-gray-800 rounded-2xl p-6">
                  <div className="space-y-4">
                    <div>
                      <div className="text-amber-400 font-semibold">Materials Used</div>
                      <div className="text-gray-300">{project.specs.materials}</div>
                    </div>
                    <div>
                      <div className="text-amber-400 font-semibold">Key Features</div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {project.specs.features.map((feature, index) => (
                          <span key={index} className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-amber-400 mb-4">Measurable Results</h3>
                <div className="bg-gray-800 rounded-2xl p-6">
                  <div className="space-y-3">
                    {project.caseStudy.measurableResults.map((result, index) => (
                      <div key={index} className="flex items-center">
                        <div className="text-amber-400 mr-3">✓</div>
                        <span className="text-gray-300">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Case Study
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Detailed analysis of project scope, challenges, solutions, and outcomes
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-2xl font-bold text-amber-400 mb-6">Project Scope</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                {project.caseStudy.scope}
              </p>
              
              <h3 className="text-2xl font-bold text-amber-400 mb-6">Challenges</h3>
              <ul className="space-y-3">
                {project.caseStudy.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start">
                    <div className="text-red-400 mr-3 mt-1">•</div>
                    <span className="text-gray-300">{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-amber-400 mb-6">Solutions</h3>
              <ul className="space-y-3 mb-8">
                {project.caseStudy.solutions.map((solution, index) => (
                  <li key={index} className="flex items-start">
                    <div className="text-green-400 mr-3 mt-1">✓</div>
                    <span className="text-gray-300">{solution}</span>
                  </li>
                ))}
              </ul>
              
              <h3 className="text-2xl font-bold text-amber-400 mb-6">Timeline</h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                {project.caseStudy.timeline}
              </p>
              
              <h3 className="text-2xl font-bold text-amber-400 mb-6">Outcomes</h3>
              <ul className="space-y-3">
                {project.caseStudy.outcomes.map((outcome, index) => (
                  <li key={index} className="flex items-start">
                    <div className="text-amber-400 mr-3 mt-1">★</div>
                    <span className="text-gray-300">{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Project Gallery
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              High-quality visuals showcasing our craftsmanship and attention to detail
            </p>
          </div>
          
          <div className="relative max-w-6xl mx-auto">
            <div className="overflow-hidden rounded-2xl shadow-2xl">
              <div className="flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${currentImage * 100}%)` }}>
                {project.gallery.map((image, index) => (
                  <div key={index} className="w-full flex-shrink-0">
                    <div 
                      className="h-[500px] bg-cover bg-center bg-no-repeat"
                      style={{ backgroundImage: `url(${image})` }}
                      title={`${project.title} - Image ${index + 1}`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={() => setCurrentImage((prev) => (prev - 1 + project.gallery.length) % project.gallery.length)}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-4 shadow-2xl transition-all duration-300 border border-white/20 backdrop-blur-sm"
              aria-label="Previous image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setCurrentImage((prev) => (prev + 1) % project.gallery.length)}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-4 shadow-2xl transition-all duration-300 border border-white/20 backdrop-blur-sm"
              aria-label="Next image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          <div className="flex justify-center mt-12 space-x-4">
            {project.gallery.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                  index === currentImage ? "bg-amber-600 border-amber-600 scale-125" : "bg-transparent border-gray-400 hover:border-white"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            Let's discuss your rough framing needs and create something exceptional together. 
            Get a free consultation and quote for your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Start Your Project
              </button>
            </Link>
            <Link to="/projects">
              <button className="bg-transparent hover:bg-gray-800 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 border-2 border-amber-600 hover:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-600/50">
                View All Projects
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail; 